import React, { useEffect, useMemo, useState } from 'react';

import {
  bulkCategorise,
  getCategoryTree,
  getProductsInCategory,
  getUncategorisedProducts,
} from '../../services/categoriesApi';

const PRODUCT_TYPE_OPTIONS = ['residential', 'commercial', 'both'];
const PAGE_SIZE = 100;

const flatten = (nodes, acc = []) => {
  for (const n of nodes) {
    acc.push(n);
    if (n.children?.length) flatten(n.children, acc);
  }
  return acc;
};

const BulkCategoriseView = () => {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  const [filterCode, setFilterCode] = useState(''); // '' = none, '__no_category' = special
  const [brandFilter, setBrandFilter] = useState('');
  const [phocasSubCategoryFilter, setPhocasSubCategoryFilter] = useState('');
  const [phocasAvailable] = useState(false); // hide filter when phocas_sku_tags empty
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [op, setOp] = useState('set_categories'); // set/add/remove or product_type-only
  const [targetCategories, setTargetCategories] = useState([]);
  const [targetProductType, setTargetProductType] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCategoryTree()
      .then((t) => {
        if (!cancelled) setTree(t);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
    return () => {
      cancelled = true;
    };
  }, []);

  const flatNodes = useMemo(() => (tree ? flatten(tree) : []), [tree]);

  const loadResults = async () => {
    setBusy(true);
    setError(null);
    try {
      let data;
      if (filterCode === '__no_category') {
        data = await getUncategorisedProducts({ limit: PAGE_SIZE });
      } else if (filterCode) {
        data = await getProductsInCategory(filterCode, { limit: PAGE_SIZE });
      } else {
        // Without a code we don't load anything - the SKU set would be 30k+.
        setResults(null);
        setSelected(new Set());
        return;
      }
      let items = data.items;
      if (brandFilter) {
        const b = brandFilter.toLowerCase();
        items = items.filter((p) => (p.brand || '').toLowerCase().includes(b));
      }
      // PHOCAS sub-category filter is hidden via the conditional in the JSX
      // when no mapping exists; the filter state stays decoupled so it's a
      // no-op until phocas_sku_tags is populated.
      if (phocasAvailable && phocasSubCategoryFilter) {
        // Placeholder: client-side filter would happen here once the data
        // shape includes phocas hints per row in the bulk list.
      }
      setResults({ ...data, items });
      setSelected(new Set(items.map((p) => p.sku)));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const apply = async (dryRun) => {
    setBusy(true);
    setError(null);
    setConfirmation(null);
    setPreview(null);
    try {
      const body = {
        skus: Array.from(selected),
        product_type: targetProductType.length ? targetProductType : null,
        dry_run: dryRun,
      };
      if (op === 'set_categories') body.set_categories = targetCategories;
      else if (op === 'add_categories') body.add_categories = targetCategories;
      else if (op === 'remove_categories') body.remove_categories = targetCategories;
      // op === 'product_type_only' leaves all category fields null
      const r = await bulkCategorise(body);
      if (dryRun) setPreview(r);
      else setConfirmation(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (sku) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(sku)) n.delete(sku);
      else n.add(sku);
      return n;
    });
  };

  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="sync-dashboard">
      <header>
        <h1>Bulk categorise</h1>
        <span className="muted">
          Filter SKUs, pick an operation, preview the impact, then apply (enqueues an Akeneo push).
        </span>
      </header>

      <section className="card">
        <h2>1. Filter SKUs</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>
            Current category:{' '}
            <select value={filterCode} onChange={(e) => setFilterCode(e.target.value)}>
              <option value="">(none — select a filter)</option>
              <option value="__no_category">No category / holding bucket</option>
              {flatNodes.map((n) => (
                <option key={n.code} value={n.code}>
                  {n.label} ({n.code})
                </option>
              ))}
            </select>
          </label>
          <label>
            Brand contains:{' '}
            <input
              type="text"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              placeholder="e.g. waterco"
            />
          </label>
          {phocasAvailable && (
            <label>
              PHOCAS sub-category:{' '}
              <input
                type="text"
                value={phocasSubCategoryFilter}
                onChange={(e) => setPhocasSubCategoryFilter(e.target.value)}
                placeholder="e.g. Single Speed"
              />
            </label>
          )}
          {!phocasAvailable && (
            <span className="muted" title="No PHOCAS mapping has been imported yet">
              PHOCAS filter hidden — <code>phocas_sku_tags</code> empty
            </span>
          )}
          <button type="button" onClick={loadResults} disabled={busy || !filterCode}>
            {busy ? 'Loading…' : 'Load SKUs'}
          </button>
        </div>
      </section>

      {results && (
        <section className="card">
          <h2>2. Pick which SKUs to include ({selected.size} of {results.items.length} selected)</h2>
          <div style={{ marginBottom: 8 }}>
            <button type="button" onClick={() => setSelected(new Set(results.items.map((p) => p.sku)))}>
              Select all
            </button>{' '}
            <button type="button" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #2a3a52' }}>
            <table>
              <thead>
                <tr><th></th><th>SKU</th><th>Name</th><th>Brand</th></tr>
              </thead>
              <tbody>
                {results.items.map((p) => (
                  <tr key={p.sku}>
                    <td><input type="checkbox" checked={selected.has(p.sku)} onChange={() => toggle(p.sku)} /></td>
                    <td><code>{p.sku}</code></td>
                    <td>{p.product_name || p.name}</td>
                    <td>{p.brand}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <h2>3. Choose operation</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            ['set_categories', 'Replace categories with…'],
            ['add_categories', 'Add to existing categories…'],
            ['remove_categories', 'Remove categories…'],
            ['product_type_only', 'Only set product_type'],
          ].map(([id, label]) => (
            <label key={id}>
              <input
                type="radio"
                name="op"
                value={id}
                checked={op === id}
                onChange={() => setOp(id)}
              />
              {' '}{label}
            </label>
          ))}
        </div>
        {op !== 'product_type_only' && (
          <div style={{ marginTop: 8 }}>
            <h3>Target categories</h3>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #2a3a52', padding: 4 }}>
              {flatNodes.map((n) => (
                <label
                  key={n.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    paddingLeft: 8 + n.depth * 12,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={targetCategories.includes(n.code)}
                    onChange={() =>
                      setTargetCategories((arr) =>
                        arr.includes(n.code) ? arr.filter((c) => c !== n.code) : [...arr, n.code],
                      )
                    }
                  />
                  <span>{n.label}</span>
                  <code style={{ color: '#7aa', fontSize: '0.85em' }}>{n.code}</code>
                </label>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <h3>product_type (optional)</h3>
          <div style={{ display: 'flex', gap: 12 }}>
            {PRODUCT_TYPE_OPTIONS.map((opt) => (
              <label key={opt}>
                <input
                  type="checkbox"
                  checked={targetProductType.includes(opt)}
                  onChange={() =>
                    setTargetProductType((arr) =>
                      arr.includes(opt) ? arr.filter((c) => c !== opt) : [...arr, opt],
                    )
                  }
                />
                {' '}{opt}
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="card">
        <h2>4. Preview &amp; apply</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => apply(true)} disabled={busy || selected.size === 0}>
            Preview (dry-run)
          </button>
          <button type="button" onClick={() => apply(false)} disabled={busy || selected.size === 0}>
            Apply & enqueue
          </button>
        </div>
        {preview && (
          <div style={{ marginTop: 8 }}>
            <p>
              Dry-run job <code>#{preview.job_id}</code>: {preview.applied} SKU
              {preview.applied === 1 ? '' : 's'} would be updated.
            </p>
            <pre style={{ maxHeight: 240, overflow: 'auto', background: '#0a1018', padding: 8 }}>
              {JSON.stringify(preview.preview, null, 2)}
            </pre>
          </div>
        )}
        {confirmation && (
          <div style={{ marginTop: 8 }}>
            <p>
              Applied to {confirmation.applied} SKUs; queued Akeneo job{' '}
              <code>#{confirmation.job_id}</code>.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default BulkCategoriseView;
